/**
 * Ask the backend whether an email address federates to an identity provider.
 *
 * Nothing about SSO is configured in the frontend: no org IDs, no provider
 * names, no URLs. The backend answers with an absolute login_url built from
 * its own SSO_SP_BASE_URL, so the same build works against a local tunnel,
 * dev, staging or prod with no rebuild.
 */
import { useEffect, useState } from 'react';
import { sso_discover } from '../../services';

const DEBOUNCE_MS = 400;
const NOT_FEDERATED = { ssoEnabled: false };

/** Cheap shape check so we don't call the API on every keystroke of "p", "pi"… */
const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');

export default function useSsoDiscovery(email) {
  const [sso, setSso] = useState(NOT_FEDERATED);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    const trimmed = (email || '').trim();
    if (!looksLikeEmail(trimmed)) {
      setSso(NOT_FEDERATED);
      setIsChecking(false);
      return undefined;
    }

    // Guards against out-of-order responses: if the user keeps typing, a slow
    // reply for an earlier address must not overwrite a newer answer.
    let cancelled = false;
    setIsChecking(true);

    const timer = setTimeout(async () => {
      try {
        const response = await sso_discover(trimmed);
        if (cancelled) return;

        const data = response?.statusCode?.response_data?.data;
        setSso(
          data?.sso_enabled
            ? {
              ssoEnabled: true,
              provider: data.provider,
              providerLabel: data.provider_label,
              enforceSso: Boolean(data.enforce_sso),
              loginUrl: data.login_url,
            }
            : NOT_FEDERATED
        );
      } catch (error) {
        // A discovery failure must never block password login -- fall back to
        // the normal form rather than stranding the user on a broken screen.
        console.error('SSO discovery failed:', error);
        if (!cancelled) setSso(NOT_FEDERATED);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email]);

  return { sso, isChecking };
}
